import React from 'react';
import {
  IoInformationCircleOutline,
  IoPersonAddOutline,
  IoMailOpenOutline,
  IoLogInOutline,
  IoShieldCheckmarkOutline,
  IoPeopleOutline,
  IoHomeOutline,
  IoVideocamOutline,
  IoCardOutline,
  IoTimeOutline,
  IoTrophyOutline,
  IoSparklesOutline,
  IoPodiumOutline,
  IoChatbubbleEllipsesOutline,
  IoSyncOutline,
  IoHelpCircleOutline,
  IoCheckmarkCircleOutline
} from 'react-icons/io5';
import './HowItWorks.css';

function HowItWorks() {
  return (
    <div className="how-page">
      {/* Hero */}
      <section className="how-hero">
        <div className="how-hero-content">
          <div className="how-hero-icon"><IoInformationCircleOutline /></div>
          <h1>How It Works</h1>
          <p className="how-hero-sub">From registration to winning the game — here’s the full flow and scoring system.</p>
        </div>
      </section>

      {/* Overview Steps */}
      <section className="how-section">
        <h2 className="how-heading">Quick Start</h2>
        <div className="how-grid">
          <div className="how-card">
            <div className="how-card-icon"><IoPersonAddOutline /></div>
            <h3>Create Your Account</h3>
            <p>Register with your name, email and password. Choose your role when invited by an admin.</p>
          </div>
          <div className="how-card">
            <div className="how-card-icon"><IoMailOpenOutline /></div>
            <h3>Verify Email</h3>
            <p>Click the verification link we send to your inbox to secure your account.</p>
          </div>
          <div className="how-card">
            <div className="how-card-icon"><IoLogInOutline /></div>
            <h3>Log In</h3>
            <p>Sign in to access live questions, leaderboards and your profile.</p>
          </div>
          <div className="how-card">
            <div className="how-card-icon"><IoShieldCheckmarkOutline /></div>
            <h3>Roles</h3>
            <p>
              Admins manage shows. Studio Contestants compete on-air. Home Viewers play from anywhere.
            </p>
          </div>
        </div>
      </section>

      {/* Joining A Game */}
      <section className="how-section">
        <h2 className="how-heading">Joining & Playing</h2>
        <div className="how-grid two-col">
          <div className="how-feature">
            <div className="how-feature-icon"><IoPeopleOutline /></div>
            <div>
              <h3>Get Invited or Join a Live Session</h3>
              <p>
                When a game session starts, questions become available in real-time. Contestants see
                questions released to the studio; home viewers see questions released to the audience at home.
              </p>
            </div>
          </div>
          <div className="how-feature">
            <div className="how-feature-icon"><IoCardOutline /></div>
            <div>
              <h3>Entry & Payments (optional)</h3>
              <p>
                Some sessions may require an entry fee. Pay securely via Stripe and your spot is confirmed instantly.
              </p>
            </div>
          </div>
          <div className="how-feature">
            <div className="how-feature-icon"><IoHomeOutline /></div>
            <div>
              <h3>Release Controls</h3>
              <p>
                Questions can be released to Contestants, Home Viewers, or both. You’ll only see and answer what’s released for your role.
              </p>
            </div>
          </div>
          <div className="how-feature">
            <div className="how-feature-icon"><IoTimeOutline /></div>
            <div>
              <h3>Timers</h3>
              <p>
                Admins can set separate timers: contestant-only time and home-viewer-only time. When time ends, results are calculated and broadcast instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Answering */}
      <section className="how-section">
        <h2 className="how-heading">Answering Questions</h2>
        <div className="how-grid three-col">
          <div className="how-card">
            <div className="how-card-icon"><IoChatbubbleEllipsesOutline /></div>
            <h3>Free-Text Answers</h3>
            <p>Type your best answer. We group similar answers using smart matching so typos and variations are handled fairly.</p>
          </div>
          <div className="how-card">
            <div className="how-card-icon"><IoSyncOutline /></div>
            <h3>Real‑Time Updates</h3>
            <p>When timers end or questions are revoked/released, your rank and score update live via WebSockets.</p>
          </div>
          <div className="how-card">
            <div className="how-card-icon"><IoShieldCheckmarkOutline /></div>
            <h3>Anti‑Spam</h3>
            <p>Duplicate or late submissions are blocked. Every answer is timestamped and tied to your user ID.</p>
          </div>
        </div>
      </section>

      {/* Scoring */}
      <section className="how-section">
        <h2 className="how-heading">Scoring & Points</h2>
        <div className="scoring-grid">
          <div className="scoring-card">
            <div className="scoring-icon"><IoSparklesOutline /></div>
            <div>
              <h3>Combined Ranking System</h3>
              <p>
                All answers are grouped by meaning. Contestants receive points based on the overall popularity ranking across all players:
                <strong> #1 = 5 pts</strong>, <strong>#2 = 4 pts</strong>, <strong>#3 = 3 pts</strong>, <strong>#4 = 2 pts</strong>, <strong>#5 = 1 pt</strong>.
              </p>
            </div>
          </div>
          <div className="scoring-card">
            <div className="scoring-icon"><IoHomeOutline /></div>
            <div>
              <h3>Home Viewers</h3>
              <p>
                Home viewers get points equal to how many home viewers gave the same answer in their group. More consensus = more points.
              </p>
            </div>
          </div>
          <div className="scoring-card">
            <div className="scoring-icon"><IoTrophyOutline /></div>
            <div>
              <h3>Leaderboards</h3>
              <p>
                View <em>Today</em> and <em>All‑time</em> leaderboards for Home and Studio. Ranks update as soon as scores change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Winning */}
      <section className="how-section">
        <h2 className="how-heading">Winning & Prizes</h2>
        <div className="how-grid two-col">
          <div className="how-feature">
            <div className="how-feature-icon"><IoPodiumOutline /></div>
            <div>
              <h3>Winners</h3>
              <p>At the end of a session, top ranks are highlighted. Admins can announce winners and prizes right in the app.</p>
            </div>
          </div>
          <div className="how-feature">
            <div className="how-feature-icon"><IoCheckmarkCircleOutline /></div>
            <div>
              <h3>Fairness</h3>
              <p>Points are recalculated consistently for everyone when results are shown, ensuring transparent outcomes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="how-section">
        <h2 className="how-heading">FAQ</h2>
        <div className="faq-list">
          <details>
            <summary><IoHelpCircleOutline /> Do I need to refresh for new scores?</summary>
            <p>No. Scores and ranks update in real‑time when timers expire or questions change.</p>
          </details>
          <details>
            <summary><IoHelpCircleOutline /> Can I change my role?</summary>
            <p>Your role is set by the admin. Reach out if you need a change.</p>
          </details>
          <details>
            <summary><IoHelpCircleOutline /> What happens if I submit twice?</summary>
            <p>Duplicate submissions are prevented. Only your first valid answer counts.</p>
          </details>
        </div>
      </section>
    </div>
  );
}

export default HowItWorks;




